import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  IconButton,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import PageModal from "../LocationsPage/PageModal";
import useFetch from "hooks/useFetch";
import styles from "./ExternalPage.module.scss";
import SearchField, { matchesSearch } from "./SearchField";

interface MyMasjidSuggestion {
  orgNr: string;
  name: string;
  address?: string | null;
  city?: string | null;
  score: number;
}

interface MyMasjidCatalogItem {
  id: number;
  guidId: string;
  name: string;
  address?: string | null;
  city?: string | null;
  imagePath?: string | null;
  imageUrl?: string | null;
  orgNr?: string | null;
  mosqueId?: number | null;
  mosqueName?: string | null;
  isActive: boolean;
  linked: boolean;
  linkedAt?: string | null;
  lastSyncedAt?: string | null;
  suggestions: MyMasjidSuggestion[];
}

interface MyMasjidStats {
  totalCatalog: number;
  linked: number;
  unlinked: number;
  activeLinked: number;
  myMasjidJamatDays: number;
  myMasjidJummahRows: number;
}

interface MyMasjidSyncLog {
  id: number;
  mode: string;
  startedAt: string;
  finishedAt: string;
  mosquesProcessed: number;
  mosquesSkipped: number;
  jamatDaysCreated: number;
  jamatDaysSkipped: number;
  jummahRowsCreated: number;
  jummahRowsReplaced: number;
  errors: string[];
  success: boolean;
}

interface MyMasjidDashboard {
  stats: MyMasjidStats;
  catalog: MyMasjidCatalogItem[];
  recentSyncLogs: MyMasjidSyncLog[];
}

interface LocationOption {
  id: number;
  name: string;
  prayerTimesTables: Array<{ id: number; name: string }>;
}

interface CreateMosqueForm {
  orgNr: string;
  name: string;
  orgName: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  locationId: string;
  prayerTimeTable: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  asrMethod: string;
}

const emptyCreateForm = (): CreateMosqueForm => ({
  orgNr: "",
  name: "",
  orgName: "",
  streetAddress: "",
  postalCode: "",
  city: "",
  locationId: "",
  prayerTimeTable: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  asrMethod: "",
});

const formatDateTime = (value?: string | null): string => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("nb-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

/** Parse norsk adresse med ev. postnr/by ut av My-Masjid address-streng. */
const parseCatalogAddress = (
  address?: string | null,
  city?: string | null,
): { streetAddress: string; postalCode: string } => {
  if (!address?.trim()) return { streetAddress: "", postalCode: "" };
  const raw = address.trim();
  const postalMatch = raw.match(/\b(\d{4})\b/);
  const postalCode = postalMatch?.[1] ?? "";
  let street = raw;
  if (postalMatch?.index != null) {
    street = raw
      .slice(0, postalMatch.index)
      .replace(/[,\s]+$/, "")
      .trim();
  }
  if (city) {
    const escapedCity = city.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const cityPattern = new RegExp(`[,\\s]*${escapedCity}\\s*$`, "i");
    street = street
      .replace(cityPattern, "")
      .replace(/[,\s]+$/, "")
      .trim();
  }
  return { streetAddress: street || raw, postalCode };
};

const suggestOrgNr = (guidId: string): string => {
  const compact = guidId.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return `MM-${compact.slice(0, 12) || "NEW"}`;
};

const MyMasjidExternalTab: React.FC = () => {
  const { execute } = useFetch();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const toast = useToast();
  const confirm = useConfirm();

  const [dashboard, setDashboard] = useState<MyMasjidDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRefreshingCatalog, setIsRefreshingCatalog] = useState(false);
  const [syncingGuid, setSyncingGuid] = useState<string | null>(null);
  const [linkingGuid, setLinkingGuid] = useState<string | null>(null);
  const [orgNrDrafts, setOrgNrDrafts] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<"all" | "unlinked" | "linked">("all");
  const [search, setSearch] = useState("");

  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [createTarget, setCreateTarget] = useState<MyMasjidCatalogItem | null>(
    null,
  );
  const [createForm, setCreateForm] =
    useState<CreateMosqueForm>(emptyCreateForm);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    const result = (await executeRef.current(
      "/api/v1/admin/my-masjid/dashboard",
      "GET",
    )) as MyMasjidDashboard | null;
    if (result) setDashboard(result);
    if (showLoading) setIsLoading(false);
  }, []);

  const fetchLocations = useCallback(async () => {
    const result = (await executeRef.current("/api/v1/locations", "GET")) as
      | LocationOption[]
      | null;
    if (result) {
      setLocations(
        [...result].sort((a, b) => a.name.localeCompare(b.name, "nb")),
      );
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchLocations();
  }, [fetchDashboard, fetchLocations]);

  const prayerTablesForSelectedLocation = useMemo(() => {
    const locationId = Number(createForm.locationId);
    if (!locationId) return [];
    return (
      locations.find((loc) => loc.id === locationId)?.prayerTimesTables ?? []
    );
  }, [createForm.locationId, locations]);

  const openCreateModal = (item: MyMasjidCatalogItem) => {
    const { streetAddress, postalCode } = parseCatalogAddress(
      item.address,
      item.city,
    );
    const cityMatch = item.city
      ? locations.find(
          (loc) =>
            loc.name.toLowerCase() === item.city!.toLowerCase() ||
            loc.name.toLowerCase().includes(item.city!.toLowerCase()),
        )
      : undefined;
    setCreateTarget(item);
    setCreateError(null);
    setCreateForm({
      orgNr: suggestOrgNr(item.guidId),
      name: item.name || "",
      orgName: item.name || "",
      streetAddress,
      postalCode,
      city: item.city || "",
      locationId: cityMatch ? String(cityMatch.id) : "",
      prayerTimeTable: cityMatch?.prayerTimesTables?.[0]
        ? String(cityMatch.prayerTimesTables[0].id)
        : "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      asrMethod: "",
    });
  };

  const closeCreateModal = () => {
    if (isCreating) return;
    setCreateTarget(null);
    setCreateForm(emptyCreateForm());
    setCreateError(null);
  };

  const updateCreateField = <K extends keyof CreateMosqueForm>(
    key: K,
    value: CreateMosqueForm[K],
  ) => {
    setCreateForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "locationId") {
        const loc = locations.find((l) => String(l.id) === value);
        next.prayerTimeTable = loc?.prayerTimesTables?.[0]
          ? String(loc.prayerTimesTables[0].id)
          : "";
      }
      return next;
    });
  };

  const handleCreateAndLink = async () => {
    if (!createTarget) return;
    const orgNr = createForm.orgNr.trim();
    const name = createForm.name.trim();
    const locationId = Number(createForm.locationId);
    if (!orgNr) {
      setCreateError("Org.nr. er påkrevd.");
      return;
    }
    if (!name) {
      setCreateError("Moskénavn er påkrevd.");
      return;
    }
    if (!locationId) {
      setCreateError("Velg lokasjon.");
      return;
    }

    setIsCreating(true);
    setCreateError(null);
    try {
      const result = (await executeRef.current(
        "/api/v1/admin/my-masjid/create-and-link",
        "POST",
        {
          guidId: createTarget.guidId,
          orgNr,
          name,
          orgName: createForm.orgName.trim() || name,
          streetAddress: createForm.streetAddress.trim() || null,
          postalCode: createForm.postalCode.trim() || null,
          city: createForm.city.trim() || null,
          locationId,
          prayerTimeTable: createForm.prayerTimeTable
            ? Number(createForm.prayerTimeTable)
            : null,
          contactName: createForm.contactName.trim() || null,
          contactPhone: createForm.contactPhone.trim() || null,
          contactEmail: createForm.contactEmail.trim() || null,
          asrMethod: createForm.asrMethod.trim() || null,
          mapOnly: false,
        },
      )) as MyMasjidCatalogItem | null;

      if (!result) {
        setCreateError(
          "Kunne ikke opprette moské. Sjekk feltene og prøv igjen.",
        );
        return;
      }

      setCreateTarget(null);
      setCreateForm(emptyCreateForm());
      await fetchDashboard(false);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSyncAll = async () => {
    setIsSyncing(true);
    try {
      await executeRef.current("/api/v1/admin/my-masjid/sync", "POST");
      await fetchDashboard(false);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRefreshCatalog = async () => {
    setIsRefreshingCatalog(true);
    try {
      const result = (await executeRef.current(
        "/api/v1/admin/my-masjid/refresh-catalog",
        "POST",
      )) as {
        citiesProcessed?: number;
        mosquesCreated?: number;
        mosquesUpdated?: number;
        mosquesUpserted?: number;
      } | null;
      await fetchDashboard(false);
      if (result) {
        toast.success(
          `Katalogen er oppdatert for Norge. Byer: ${
            result.citiesProcessed ?? 0
          }, nye: ${result.mosquesCreated ?? 0}, oppdatert: ${
            result.mosquesUpdated ?? 0
          }, totalt: ${result.mosquesUpserted ?? 0}.`,
        );
      }
    } finally {
      setIsRefreshingCatalog(false);
    }
  };

  const handleSyncOne = async (guidId: string) => {
    setSyncingGuid(guidId);
    try {
      await executeRef.current(
        `/api/v1/admin/my-masjid/sync/${guidId}`,
        "POST",
      );
      await fetchDashboard(false);
    } finally {
      setSyncingGuid(null);
    }
  };

  const handleLink = async (guidId: string, orgNr?: string) => {
    const value = (orgNr ?? orgNrDrafts[guidId] ?? "").trim();
    if (!value) {
      toast.error("Skriv inn org.nr. for å koble moskeen.");
      return;
    }
    setLinkingGuid(guidId);
    try {
      await executeRef.current("/api/v1/admin/my-masjid/link", "POST", {
        guidId,
        orgNr: value,
      });
      setOrgNrDrafts((prev) => {
        const next = { ...prev };
        delete next[guidId];
        return next;
      });
      await fetchDashboard(false);
    } finally {
      setLinkingGuid(null);
    }
  };

  const handleUnlink = async (guidId: string) => {
    const ok = await confirm({
      title: "Fjern kobling?",
      message: "Koblingen mellom My-Masjid-moskeen og org.nr. fjernes.",
      confirmLabel: "Fjern kobling",
      tone: "danger",
    });
    if (!ok) return;
    setLinkingGuid(guidId);
    try {
      await executeRef.current(
        `/api/v1/admin/my-masjid/link/${guidId}`,
        "DELETE",
      );
      await fetchDashboard(false);
    } finally {
      setLinkingGuid(null);
    }
  };

  const stats = dashboard?.stats;
  const items = (dashboard?.catalog ?? []).filter((item) => {
    if (filter === "unlinked" && item.linked) return false;
    if (filter === "linked" && !item.linked) return false;
    return matchesSearch(search, [
      item.name,
      item.city,
      item.address,
      item.orgNr,
      item.mosqueName,
      item.guidId,
    ]);
  });

  const nf = (n: number | undefined) => (n ?? 0).toLocaleString("nb-NO");

  if (isLoading && !dashboard) {
    return (
      <>
        <div className={styles.statsGrid}>
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} height={84} radius={18} />
          ))}
        </div>
        <Skeleton height={240} radius={20} />
      </>
    );
  }

  if (!dashboard) {
    return (
      <Card>
        <EmptyState icon="alert" title="Kunne ikke laste My-Masjid-oversikten">
          Prøv å oppdatere siden.
        </EmptyState>
      </Card>
    );
  }

  const catalogColumns: Array<Column<MyMasjidCatalogItem>> = [
    {
      key: "name",
      header: "My-Masjid",
      render: (item) => (
        <span className={styles.mmCell}>
          {item.imageUrl ? (
            <img src={item.imageUrl} alt="" className={styles.thumb} />
          ) : (
            <span className={styles.thumb} aria-hidden="true">
              <Icon name="mosque" size={18} />
            </span>
          )}
          <span className={styles.mmText}>
            <b>{item.name}</b>
            <span className={styles.guid} title={item.guidId}>
              {item.guidId.length > 10
                ? `${item.guidId.slice(0, 4)}-…-${item.guidId.slice(-2)}`
                : item.guidId}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: "address",
      header: "Adresse",
      render: (item) => (
        <span className={styles.mmText}>
          <span className={styles.muted}>{item.address || "—"}</span>
          {item.city && <span className={styles.muted}>{item.city}</span>}
        </span>
      ),
    },
    {
      key: "link",
      header: "Forslag / org.nr.",
      width: 320,
      render: (item) => {
        const draft = orgNrDrafts[item.guidId] ?? item.orgNr ?? "";
        if (item.linked) {
          return (
            <span className={styles.mmText}>
              <span className={styles.inline}>
                <Badge tone="mint">Koblet</Badge>
                <span className={styles.muted}>{item.orgNr}</span>
              </span>
              {item.mosqueName && (
                <span className={styles.muted}>{item.mosqueName}</span>
              )}
            </span>
          );
        }
        return (
          <div className={styles.linkBox}>
            {item.suggestions.length > 0 && (
              <div className={styles.inline}>
                {item.suggestions.map((s) => (
                  <button
                    key={s.orgNr}
                    type="button"
                    className={draft === s.orgNr ? styles.pillOn : styles.pill}
                    title={`${s.name} — ${s.address || ""} (score ${s.score})`}
                    onClick={() =>
                      setOrgNrDrafts((prev) => ({
                        ...prev,
                        [item.guidId]: s.orgNr,
                      }))
                    }
                  >
                    {s.name} ({s.orgNr})
                  </button>
                ))}
              </div>
            )}
            <div className={styles.inlineNoWrap}>
              <TextField
                aria-label="Org.nr."
                placeholder="Org.nr."
                className={styles.orgInput}
                fieldClassName={styles.orgField}
                value={draft}
                onChange={(e) =>
                  setOrgNrDrafts((prev) => ({
                    ...prev,
                    [item.guidId]: e.target.value,
                  }))
                }
              />
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="link" size={14} />}
                loading={linkingGuid === item.guidId}
                onClick={() => handleLink(item.guidId)}
                title="Koble til eksisterende org.nr."
              >
                Koble
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={() => openCreateModal(item)}
              >
                Opprett i iBønn
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      render: (item) =>
        item.linked ? (
          <Badge tone={item.isActive ? "mint" : "grey"}>
            {item.isActive ? "Aktiv sync" : "Pauset"}
          </Badge>
        ) : (
          <Badge tone="gold">Mangler org.nr.</Badge>
        ),
    },
    {
      key: "last",
      header: "Siste sync",
      numeric: true,
      render: (item) => (
        <span className={styles.muted}>
          {formatDateTime(item.lastSyncedAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (item) =>
        item.linked && (
          <span className={styles.inlineNoWrap}>
            <IconButton
              label="Sync hele årets iqamah og jummah"
              size="sm"
              icon={<Icon name="swap" size={16} />}
              loading={syncingGuid === item.guidId}
              disabled={syncingGuid === item.guidId || isSyncing}
              onClick={() => handleSyncOne(item.guidId)}
            />
            <IconButton
              label="Fjern kobling"
              size="sm"
              icon={<Icon name="x" size={16} />}
              disabled={linkingGuid === item.guidId}
              onClick={() => handleUnlink(item.guidId)}
            />
          </span>
        ),
    },
  ];

  const logColumns: Array<Column<MyMasjidSyncLog>> = [
    {
      key: "time",
      header: "Tid",
      numeric: true,
      render: (log) => (
        <span className={styles.muted}>{formatDateTime(log.startedAt)}</span>
      ),
    },
    {
      key: "mode",
      header: "Modus",
      render: (log) => <Badge tone="grey">{log.mode}</Badge>,
    },
    {
      key: "status",
      header: "Status",
      render: (log) => (
        <Badge tone={log.success ? "mint" : "rose"}>
          {log.success ? "OK" : "Med feil"}
        </Badge>
      ),
    },
    {
      key: "mosques",
      header: "Moskeer",
      render: (log) => (
        <>
          {log.mosquesProcessed}
          {log.mosquesSkipped > 0 && (
            <span className={styles.muted}>
              {" "}
              (+{log.mosquesSkipped} hoppet over)
            </span>
          )}
        </>
      ),
    },
    {
      key: "jamat",
      header: "Jamat",
      render: (log) => `+${log.jamatDaysCreated}`,
    },
    {
      key: "jummah",
      header: "Jummah",
      render: (log) => `+${log.jummahRowsCreated}`,
    },
    {
      key: "errors",
      header: "Feil",
      render: (log) =>
        log.errors.length === 0 ? (
          <span className={styles.muted}>–</span>
        ) : (
          <ul className={styles.errorList}>
            {log.errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
    },
  ];

  return (
    <>
      <div className={styles.toolbar}>
        <SegmentedControl
          label="Filter"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: `Alle (${stats?.totalCatalog ?? 0})` },
            {
              value: "unlinked",
              label: `Ikke i iBønn / ukoblet (${stats?.unlinked ?? 0})`,
            },
            { value: "linked", label: `Koblet (${stats?.linked ?? 0})` },
          ]}
        />
        <span className={styles.toolbarActions}>
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={() => fetchDashboard()}
            loading={isLoading}
            disabled={isLoading || isSyncing || isRefreshingCatalog}
          >
            Oppdater
          </Button>
          <Button
            variant="secondary"
            icon={<Icon name="download" size={16} />}
            onClick={handleRefreshCatalog}
            loading={isRefreshingCatalog}
            disabled={isRefreshingCatalog || isSyncing}
          >
            {isRefreshingCatalog ? "Henter Norge …" : "Hent alle byer i Norge"}
          </Button>
          <Button
            icon={<Icon name="spark" size={16} />}
            onClick={handleSyncAll}
            loading={isSyncing}
            disabled={
              isSyncing ||
              isRefreshingCatalog ||
              (stats?.activeLinked ?? 0) === 0
            }
          >
            {isSyncing ? "Synkroniserer …" : "Sync iqamah for året"}
          </Button>
        </span>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.stat}>
          <b>{nf(stats?.totalCatalog)}</b>
          <span>I katalog (Norge)</span>
        </div>
        <div className={styles.stat}>
          <b>{nf(stats?.unlinked)}</b>
          <span>Mangler org.nr.-kobling</span>
        </div>
        <div className={styles.stat}>
          <b>{nf(stats?.activeLinked)}</b>
          <span>Aktive for sync</span>
        </div>
        <div className={styles.stat}>
          <b>{nf(stats?.myMasjidJamatDays)}</b>
          <span>Jamat-dager (MY_MASJID)</span>
        </div>
        <div className={styles.stat}>
          <b>{nf(stats?.myMasjidJummahRows)}</b>
          <span>Jummah-rader (MY_MASJID)</span>
        </div>
      </div>

      <Card
        title="My-Masjid-katalog"
        subtitle="Velg et forslag eller skriv org.nr. manuelt for å koble moskeen. Mangler moskeen i iBønn: bruk «Opprett i iBønn». Sync kjører bare for koblede moskeer og overskriver ikke ADMIN/Bonnetid."
      >
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder="Søk etter moské, by, adresse eller org.nr. …"
        />
        <Table
          columns={catalogColumns}
          rows={items}
          rowKey={(item) => item.guidId}
          empty={
            <EmptyState
              icon="mosque"
              title={
                search.trim()
                  ? `Ingen treff på «${search.trim()}»`
                  : "Ingen moskeer i dette filteret"
              }
            />
          }
        />
      </Card>

      <Card title="My-Masjid sync-logg">
        <Table
          columns={logColumns}
          rows={dashboard.recentSyncLogs}
          rowKey={(log) => `${log.id}-${log.startedAt}`}
          empty={
            <EmptyState icon="doc" title="Ingen synkroniseringslogger ennå" />
          }
        />
      </Card>

      <PageModal
        open={!!createTarget}
        title="Opprett moské i iBønn"
        icon="mosque"
        width={720}
        onClose={closeCreateModal}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeCreateModal}
              disabled={isCreating}
            >
              Avbryt
            </Button>
            <Button
              icon={<Icon name="plus" size={16} />}
              onClick={handleCreateAndLink}
              loading={isCreating}
            >
              {isCreating ? "Oppretter …" : "Opprett og koble"}
            </Button>
          </>
        }
      >
        {createTarget && (
          <>
            <div className={styles.createIntro}>
              {createTarget.imageUrl && (
                <img
                  src={createTarget.imageUrl}
                  alt={createTarget.name}
                  className={styles.createImage}
                />
              )}
              <span className={styles.muted}>
                Prefylt fra My-Masjid: <b>{createTarget.name}</b>
                {createTarget.city ? ` (${createTarget.city})` : ""}. Fyll inn
                det som mangler, spesielt lokasjon og org.nr., før du lagrer.
                Logoen hentes automatisk fra My-Masjid.
              </span>
            </div>
            {createError && <Alert tone="error">{createError}</Alert>}
            <div className={styles.formGrid}>
              <TextField
                label="Org.nr."
                required
                value={createForm.orgNr}
                onChange={(e) => updateCreateField("orgNr", e.target.value)}
                placeholder="F.eks. BRREG eller MM-…"
                help="Forslaget er generert. Bytt til ekte org.nr. hvis du har det."
              />
              <Select
                label="Lokasjon"
                required
                value={createForm.locationId}
                onChange={(e) =>
                  updateCreateField("locationId", e.target.value)
                }
              >
                <option value="">Velg lokasjon</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </Select>
              <TextField
                label="Moskénavn"
                required
                value={createForm.name}
                onChange={(e) => updateCreateField("name", e.target.value)}
              />
              <TextField
                label="Organisasjonsnavn"
                value={createForm.orgName}
                onChange={(e) => updateCreateField("orgName", e.target.value)}
              />
            </div>
            <div className={styles.addressGrid}>
              <TextField
                label="Adresse"
                value={createForm.streetAddress}
                onChange={(e) =>
                  updateCreateField("streetAddress", e.target.value)
                }
              />
              <TextField
                label="Postnr."
                value={createForm.postalCode}
                onChange={(e) =>
                  updateCreateField("postalCode", e.target.value)
                }
              />
            </div>
            <div className={styles.formGrid}>
              <TextField
                label="Sted"
                value={createForm.city}
                onChange={(e) => updateCreateField("city", e.target.value)}
              />
              <Select
                label="Bønnetabell"
                value={createForm.prayerTimeTable}
                onChange={(e) =>
                  updateCreateField("prayerTimeTable", e.target.value)
                }
                disabled={prayerTablesForSelectedLocation.length === 0}
              >
                <option value="">Standard</option>
                {prayerTablesForSelectedLocation.map((table) => (
                  <option key={table.id} value={table.id}>
                    {table.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className={styles.contactGrid}>
              <TextField
                label="Kontaktperson"
                value={createForm.contactName}
                onChange={(e) =>
                  updateCreateField("contactName", e.target.value)
                }
              />
              <TextField
                label="Telefon"
                value={createForm.contactPhone}
                onChange={(e) =>
                  updateCreateField("contactPhone", e.target.value)
                }
              />
              <TextField
                label="E-post"
                type="email"
                value={createForm.contactEmail}
                onChange={(e) =>
                  updateCreateField("contactEmail", e.target.value)
                }
              />
            </div>
            <div className={styles.formGrid}>
              <Select
                label="Asr-metode"
                value={createForm.asrMethod}
                onChange={(e) => updateCreateField("asrMethod", e.target.value)}
              >
                <option value="">Ikke satt</option>
                <option value="ONE_X">ONE_X (1×)</option>
                <option value="TWO_X">TWO_X (2×)</option>
              </Select>
            </div>
          </>
        )}
      </PageModal>
    </>
  );
};

export default MyMasjidExternalTab;
